import { useState } from 'react'
import { Field, fieldClass, Hint, Actions, DangerZone } from './Drawer'
import { CALIB_STEP, PRODUCTS, fmtLitres, remainingProducts, sellingLabel } from './constants'

function Stepper({ value, onChange, min = 1, max = 99, unit }) {
  const set = (v) => onChange(Math.min(max, Math.max(min, v)))
  const btn = 'w-9 bg-gray-100 text-base font-semibold text-gray-700 transition hover:bg-gray-200 disabled:opacity-35'
  return (
    <div className="inline-flex overflow-hidden rounded-[10px] border border-gray-200 bg-gray-50">
      <button type="button" className={btn} disabled={value <= min} onClick={() => set(value - 1)}>−</button>
      <span className="min-w-[96px] py-2.5 text-center font-mono text-[13.5px] font-semibold tabular-nums text-gray-900">{value} {unit}</span>
      <button type="button" className={btn} disabled={value >= max} onClick={() => set(value + 1)}>+</button>
    </div>
  )
}

/** Free numeric entry — the station sets its own figure, 0 upward, no ceiling. */
function CalibrationStepper({ value, onChange }) {
  const nudge = (d) => onChange(String(Math.max(0, Math.round((Number(value || 0) + d) * 100) / 100).toFixed(2)))
  const btn = 'w-9 bg-gray-100 text-base font-semibold text-gray-700 transition hover:bg-gray-200'
  return (
    <>
      <div className="inline-flex items-stretch overflow-hidden rounded-[10px] border border-gray-200 bg-gray-50">
        <button type="button" className={btn} onClick={() => nudge(-CALIB_STEP)}>−</button>
        <span className="flex items-center">
          <input
            inputMode="decimal"
            aria-label="Calibration excess in litres"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-[74px] border-0 bg-transparent py-2.5 text-right font-mono text-[13.5px] font-semibold tabular-nums text-gray-900 outline-none"
          />
          <span className="pr-3 font-mono text-[12.5px] text-gray-400">L</span>
        </span>
        <button type="button" className={btn} onClick={() => nudge(CALIB_STEP)}>+</button>
      </div>
      <p className="mt-1.5 text-[11.5px] text-gray-500">
        Type any figure the station uses — 0 means no excess. The ± buttons nudge by {CALIB_STEP} L.
      </p>
    </>
  )
}

export function StationForm({ station, onSave, onCancel, onDelete, busy }) {
  const [name, setName] = useState(station.name || '')
  const [location, setLocation] = useState(station.location || '')
  return (
    <>
      <Field label="Station name"><input className={fieldClass} value={name} onChange={(e) => setName(e.target.value)} /></Field>
      <Field label="Location"><input className={fieldClass} value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. Lagos · Ikeja" /></Field>
      <Actions busy={busy} onCancel={onCancel} onSave={() => onSave({ name, location })} />
      <DangerZone
        busy={busy}
        label="Delete station"
        note="Deletes the station along with its tanks, pumps and every reading recorded there. This cannot be undone."
        onConfirm={onDelete}
      />
    </>
  )
}

export function NewStationForm({ orgName, orgOptions = [], defaultOrgId, onSave, onCancel, busy }) {
  const [name, setName] = useState('')
  const [location, setLocation] = useState('')
  // A superadmin spans organisations, so they have to say which one.
  const [organizationId, setOrganizationId] = useState(defaultOrgId || orgOptions[0]?.id || '')
  const chooseOrg = orgOptions.length > 1

  return (
    <>
      <Hint>
        {chooseOrg
          ? 'This creates a station under the organisation you pick. Add its product lines right after, without leaving this screen.'
          : `This creates a station under ${orgName}. Add its product lines right after, without leaving this screen.`}
      </Hint>
      {chooseOrg && (
        <Field label="Organisation">
          <select className={fieldClass} value={organizationId} onChange={(e) => setOrganizationId(e.target.value)}>
            {orgOptions.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </Field>
      )}
      <Field label="Station name"><input className={fieldClass} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Ojota Station" /></Field>
      <Field label="Location"><input className={fieldClass} value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. Lagos · Ojota" /></Field>
      <Actions busy={busy} saveLabel="Create station" onCancel={onCancel} onSave={() => onSave({ name, location, organizationId })} />
    </>
  )
}

export function NewLineForm({ station, onSave, onCancel, busy }) {
  const remaining = remainingProducts(station)
  const [product, setProduct] = useState(remaining[0] || '')
  const [tankCount, setTankCount] = useState(2)
  const [capacity, setCapacity] = useState('33000')
  const [pumpCount, setPumpCount] = useState(4)

  if (remaining.length === 0) {
    return <p className="text-[13.5px] text-gray-500">Every product already has a line at this station.</p>
  }

  return (
    <>
      <Hint>
        One product line = its storage tanks + the pumps that sell it. All pumps sit on one shared
        pipe, so any pump can sell from whichever tank&rsquo;s valve is open.
      </Hint>
      <Field label="Product">
        <div className="flex flex-wrap gap-2">
          {remaining.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setProduct(p)}
              style={product === p ? { background: PRODUCTS[p].color, borderColor: 'transparent', color: '#fff' } : undefined}
              className="rounded-[9px] border-[1.5px] border-gray-200 bg-gray-50 px-3 py-1.5 font-mono text-[12.5px] font-semibold text-gray-600"
            >
              {p}
            </button>
          ))}
        </div>
      </Field>
      <Field label="How many tanks"><Stepper value={tankCount} onChange={setTankCount} min={1} max={6} unit={tankCount === 1 ? 'tank' : 'tanks'} /></Field>
      <Field label="Capacity per tank (litres)">
        <input className={`${fieldClass} font-mono tabular-nums`} inputMode="numeric" value={capacity} onChange={(e) => setCapacity(e.target.value)} />
      </Field>
      <Field label="How many pumps"><Stepper value={pumpCount} onChange={setPumpCount} min={1} max={12} unit={pumpCount === 1 ? 'pump' : 'pumps'} /></Field>
      <Actions
        busy={busy}
        saveLabel="Create product line"
        onCancel={onCancel}
        onSave={() => onSave({ product, tankCount, capacity: Number(capacity), pumpCount })}
      />
    </>
  )
}

export function TankForm({ tank, line, onSave, onCancel, onToggleValve, onRetire, busy }) {
  const [capacity, setCapacity] = useState(String(tank.capacity ?? ''))
  const selling = sellingLabel(line.tanks)
  return (
    <>
      <Field label="Tank number"><input className={`${fieldClass} font-mono tabular-nums opacity-60`} value={tank.tankNumber} disabled /></Field>
      <Field label="Product"><input className={`${fieldClass} opacity-60`} value={`${line.product} · ${PRODUCTS[line.product]?.name || ''}`} disabled /></Field>
      <Field label="Capacity (litres)">
        <input className={`${fieldClass} font-mono tabular-nums`} inputMode="numeric" value={capacity} onChange={(e) => setCapacity(e.target.value)} />
      </Field>

      <Field label="Valve">
        <Hint>
          {tank.valveOpen
            ? `Open — this tank is feeding the ${line.product} pumps. More than one tank can sell at the same time.`
            : 'Closed — held as reserve. The pumps are drawing from another tank.'}
          {selling && ` Currently selling from ${selling}.`}
        </Hint>
        <button
          type="button"
          onClick={onToggleValve}
          disabled={busy}
          className="w-full rounded-[9px] border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:opacity-60"
        >
          {tank.valveOpen ? 'Close valve (put on reserve)' : 'Open valve (start selling)'}
        </button>
      </Field>

      <Actions busy={busy} onCancel={onCancel} onSave={() => onSave({ capacity: Number(capacity) })} />
      <DangerZone
        busy={busy}
        label="Retire tank"
        note="Takes the tank out of service. Its dip readings are kept, so past days still reconcile."
        onConfirm={onRetire}
      />
    </>
  )
}

export function NewTankForm({ line, station, nextNumber, onSave, onCancel, busy }) {
  const [capacity, setCapacity] = useState('33000')
  return (
    <>
      <Hint>
        Adds a tank to the {line.product} line at {station.name}. It joins the same shared pipe — no
        pump wiring needed. New tanks start as reserve, with the valve closed.
      </Hint>
      <Field label="Tank number"><input className={`${fieldClass} font-mono tabular-nums opacity-60`} value={nextNumber} disabled /></Field>
      <Field label="Capacity (litres)">
        <input className={`${fieldClass} font-mono tabular-nums`} inputMode="numeric" value={capacity} onChange={(e) => setCapacity(e.target.value)} />
      </Field>
      <Actions busy={busy} saveLabel="Create tank" onCancel={onCancel} onSave={() => onSave({ capacity: Number(capacity) })} />
    </>
  )
}

export function PumpForm({ pump, line, onSave, onCancel, onRetire, busy }) {
  const [name, setName] = useState(pump.name || '')
  const [calib, setCalib] = useState(Number(pump.defaultExcessPer20L ?? 0).toFixed(2))
  return (
    <>
      <Field label="Pump name"><input className={fieldClass} value={name} onChange={(e) => setName(e.target.value)} /></Field>
      <Field label="Product line">
        <input className={`${fieldClass} opacity-60`} value={`${line.product} · sells from the open tank`} disabled />
      </Field>
      <Field label="Calibration — excess per 20L">
        <CalibrationStepper value={calib} onChange={setCalib} />
      </Field>
      <Actions busy={busy} onCancel={onCancel} onSave={() => onSave({ name, calibration: Number(calib) })} />
      <DangerZone
        busy={busy}
        label="Retire pump"
        note="Takes the pump out of service. Its meter readings are kept."
        onConfirm={onRetire}
      />
    </>
  )
}

export function NewPumpForm({ line, station, nextNumber, onSave, onCancel, busy }) {
  const [name, setName] = useState(`Pump ${nextNumber}`)
  return (
    <>
      <Hint>
        Adds a pump to the {line.product} shared line at {station.name}. It can sell from any
        {' '}{line.product} tank — whichever valve is open.
      </Hint>
      <Field label="Pump name"><input className={fieldClass} value={name} onChange={(e) => setName(e.target.value)} /></Field>
      <Actions busy={busy} saveLabel="Create pump" onCancel={onCancel} onSave={() => onSave({ name })} />
    </>
  )
}

export function LineForm({ line, station, onCancel, onRetire, busy }) {
  const capacity = line.tanks.reduce((s, t) => s + (Number(t.capacity) || 0), 0)
  return (
    <>
      <Hint>
        {line.tanks.length} tank{line.tanks.length === 1 ? '' : 's'} holding {fmtLitres(capacity)} L,
        sold through {line.pumps.length} pump{line.pumps.length === 1 ? '' : 's'} on one shared pipe.
        Tanks and pumps are edited individually — click any silo or pump chip.
      </Hint>
      <Actions busy={busy} saveLabel="Done" onCancel={onCancel} onSave={onCancel} />
      <DangerZone
        busy={busy}
        label={`Retire the whole ${line.product} line`}
        note={`Takes every ${line.product} tank and pump at ${station.name} out of service. Their readings are kept, so past days still reconcile.`}
        onConfirm={onRetire}
      />
    </>
  )
}
